import type {
  ProviderInstanceId,
  ProviderOptionSelection,
  ServerProviderModel,
} from "@afar-research/contracts";
import { BotIcon, TriangleAlertIcon, XIcon } from "lucide-react";

import {
  describeProjectCreationProviderRecovery,
  type ProjectCreationProviderChoice,
  type ProjectCreationProviderReadiness,
} from "../projectCreationProviderSelection";
import type { ProviderInstanceEntry } from "../providerInstances";
import { ProviderModelPicker } from "./chat/ProviderModelPicker";
import { TraitsPicker } from "./chat/TraitsPicker";
import { Button } from "./ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "./ui/dialog";

/**
 * What a project dialog knows about the provider it opens a thread on: the
 * Settings default, and the ready instances it may fall back to
 * (`useProjectProviderChoice`).
 */
export interface ProjectProviderProps {
  readonly providerReadiness: ProjectCreationProviderReadiness;
  /** A Cloud Workspace, whose Agent Providers AFAR manages. */
  readonly managedByAfar: boolean;
  readonly instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  /** What the fallback picker offers: only instances that can start the thread now. */
  readonly fallbackInstanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  readonly modelOptionsByInstance: ReadonlyMap<
    ProviderInstanceId,
    ReadonlyArray<ServerProviderModel>
  >;
  /** Instances that can start the thread. */
  readonly eligibleInstanceIds: ReadonlySet<ProviderInstanceId>;
  readonly getInstanceDisabledReason: (instanceId: ProviderInstanceId) => string | null;
  /** Why the Settings default cannot start the thread, or `null` when it can. */
  readonly providerFallbackReason: string | null;
  readonly selection: ProjectCreationProviderChoice | null;
  readonly onSelectionChange: (selection: ProjectCreationProviderChoice) => void;
  /** Traits a freshly chosen instance and model start with. */
  readonly getDefaultProviderOptions: (chosen: {
    readonly instanceId: ProviderInstanceId;
    readonly model: string;
  }) => ReadonlyArray<ProviderOptionSelection> | undefined;
  /** Absent where the Environment offers no provider settings (a Cloud Workspace). */
  readonly onOpenProviderSettings?: (() => void) | undefined;
}

/** Whether the chosen instance can start the thread right now. */
export function projectProviderReady(props: ProjectProviderProps): boolean {
  return props.selection !== null && props.eligibleInstanceIds.has(props.selection.instanceId);
}

function selectedEntry(props: ProjectProviderProps) {
  return props.instanceEntries.find((entry) => entry.instanceId === props.selection?.instanceId);
}

/** The whole dialog body while no instance can start the thread at all. */
export function ProjectProviderRecovery(props: {
  readonly provider: ProjectProviderProps;
  readonly title: string;
  readonly description: string;
  readonly onCancel: () => void;
}) {
  const recovery = describeProjectCreationProviderRecovery(
    {
      readiness: props.provider.providerReadiness,
      instanceEntries: props.provider.instanceEntries,
    },
    { managedByAfar: props.provider.managedByAfar },
  );
  return (
    <>
      <DialogHeader className="pe-14">
        <DialogTitle>{props.title}</DialogTitle>
        <DialogDescription>{props.description}</DialogDescription>
        <Button
          aria-label="Close"
          className="absolute end-2 top-2"
          onClick={props.onCancel}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon />
        </Button>
      </DialogHeader>
      <DialogPanel>
        <div className="rounded-xl border bg-muted/40 p-4" role="status">
          <div className="font-medium">{recovery.headline}</div>
          <p className="mt-1 text-muted-foreground text-sm">{recovery.detail}</p>
        </div>
      </DialogPanel>
      <DialogFooter>
        <Button onClick={props.onCancel} type="button" variant="outline">
          Cancel
        </Button>
        {recovery.offersProviderSettings && props.provider.onOpenProviderSettings ? (
          <Button onClick={props.provider.onOpenProviderSettings} type="button">
            Open Provider Settings
          </Button>
        ) : null}
      </DialogFooter>
    </>
  );
}

/** The callout that asks for another ready instance when the Settings default cannot start. */
export function ProjectProviderFallback(props: {
  readonly provider: ProjectProviderProps;
  readonly locked: boolean;
}) {
  const { provider } = props;
  if (provider.providerFallbackReason === null) return null;
  const entry = selectedEntry(provider);
  return (
    <div className="grid gap-2 rounded-xl border bg-muted/30 p-3">
      <div className="flex gap-2 text-sm">
        <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-warning" />
        <p>{provider.providerFallbackReason}</p>
      </div>
      <ProviderModelPicker
        activeInstanceId={provider.selection?.instanceId ?? null}
        ariaLabel="Provider Instance"
        disabled={props.locked}
        getModelDisabledReason={provider.getInstanceDisabledReason}
        instanceEntries={provider.fallbackInstanceEntries}
        lockedProvider={null}
        model={provider.selection?.model ?? ""}
        modelOptionsByInstance={provider.modelOptionsByInstance}
        onInstanceModelChange={(instanceId, model) =>
          provider.onSelectionChange({
            instanceId,
            model,
            options: provider.getDefaultProviderOptions({ instanceId, model }),
          })
        }
        triggerClassName="h-9 w-full max-w-none justify-between bg-background text-foreground"
        triggerVariant="outline"
        {...(projectProviderReady(provider) ? {} : { triggerLabel: "Choose a Provider Instance" })}
      />
      {entry !== undefined && provider.selection !== null ? (
        <TraitsPicker
          disabled={props.locked}
          instanceId={provider.selection.instanceId}
          model={provider.selection.model}
          modelOptions={provider.selection.options}
          models={entry.models}
          onModelOptionsChange={(options) => {
            if (provider.selection === null) return;
            provider.onSelectionChange({ ...provider.selection, options });
          }}
          provider={entry.driverKind}
          triggerClassName="h-8 justify-start"
          triggerVariant="outline"
        />
      ) : null}
    </div>
  );
}

/** The quiet footer note naming the Settings default the thread opens on. */
export function ProjectProviderDefaultLabel(props: { readonly provider: ProjectProviderProps }) {
  const { provider } = props;
  const entry = selectedEntry(provider);
  if (
    provider.providerFallbackReason !== null ||
    entry === undefined ||
    provider.selection === null
  ) {
    return null;
  }
  return (
    <p
      className="flex min-w-0 items-center gap-1.5 text-muted-foreground text-xs sm:me-auto"
      title="The default Agent Provider in Settings"
    >
      <BotIcon className="size-3.5 shrink-0" />
      <span className="truncate">
        {entry.displayName} · {provider.selection.model}
      </span>
    </p>
  );
}
